import React, { ReactNode, useCallback, useMemo } from 'react';
import {
  CardContent,
  CardHeader,
  Link,
  Skeleton,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Typography,
} from '@mui/material';
import { useQuery } from '@tanstack/react-query';
import { TrendingDownRounded, TrendingUpRounded } from '@mui/icons-material';
import { deals, DealTypes } from 'api';
import { useStoreContext } from 'store';
import { colors } from 'theme/constants';
import { appLinks } from 'routes/links';
import { formatCurrency, formatPercent } from 'helpers/formatters';
import { getAttributionModelField } from 'helpers/common';
import { TooltipChip, ColorCard } from 'components';

interface Props {
  deal: DealTypes.ItemDataExtended;
}

type InfoItems = {
  key: keyof DealTypes.ItemData;
  label: string;
  labelCaption?: string;
  prevValue: ReactNode;
  currentValue: ReactNode;
  trend: 'up' | 'down';
}[];

const DealComparisonCard = (props: Props): JSX.Element | null => {
  const { deal } = props;
  const {
    state: { activeCountry, activeBrand, attributionModel },
  } = useStoreContext();

  const filtersValues: DealTypes.GetListParams['filters'] = useMemo(
    () => ({
      brand: activeBrand,
      country_team: activeCountry,
      influencer_id: deal.influencer_id.$oid,
      status: [DealTypes.StatusEnum.Posted, DealTypes.StatusEnum.Finished],
    }),
    [activeBrand, activeCountry, deal.influencer_id.$oid],
  );

  const { data, error } = useQuery(
    [deals.endpoints.getList, filtersValues],
    () =>
      deals
        .getList({ filters: filtersValues, limit: 2, page: 1 })
        .then((res) => res.data[1]),
  );

  const getTrend = useCallback(
    (key: keyof DealTypes.ItemData) =>
      (deal[key] || 0) > (data ? data[key] : 0) ? 'up' : 'down',
    [deal, data],
  );

  const tableFields: InfoItems = useMemo(
    () => [
      {
        key: 'budget_actual',
        label: 'Budget',
        prevValue: formatCurrency(data?.budget_actual || 0),
        currentValue: formatCurrency(deal.budget_actual || 0),
        trend: getTrend('budget_actual'),
      },
      {
        key: getAttributionModelField(attributionModel, 'revenue_actual'),
        label: 'Revenue',
        labelCaption: `(${attributionModel})`,
        prevValue: formatCurrency(
          data?.[
            getAttributionModelField(attributionModel, 'revenue_actual')
          ] || 0,
        ),
        currentValue: formatCurrency(
          deal[getAttributionModelField(attributionModel, 'revenue_actual')] ||
            0,
        ),
        trend: getTrend(
          getAttributionModelField(attributionModel, 'revenue_actual'),
        ),
      },
      {
        key: getAttributionModelField(attributionModel, 'roi_actual'),
        label: 'ROI',
        labelCaption: `(${attributionModel})`,
        prevValue:
          data?.[getAttributionModelField(attributionModel, 'roi_actual')] || 0,
        currentValue:
          deal[getAttributionModelField(attributionModel, 'roi_actual')] || 0,
        trend: getTrend(
          getAttributionModelField(attributionModel, 'roi_actual'),
        ),
      },
      {
        key: getAttributionModelField(attributionModel, 'cm3_actual'),
        label: 'CM3',
        labelCaption: `(${attributionModel})`,
        prevValue: formatCurrency(
          data?.[getAttributionModelField(attributionModel, 'cm3_actual')] || 0,
        ),
        currentValue: formatCurrency(
          deal[getAttributionModelField(attributionModel, 'cm3_actual')] || 0,
        ),
        trend: getTrend(
          getAttributionModelField(attributionModel, 'cm3_actual'),
        ),
      },
      {
        key: getAttributionModelField(attributionModel, 'cm3_actual_pc'),
        label: 'CM3%',
        labelCaption: `(${attributionModel})`,
        prevValue: formatPercent(
          data?.[getAttributionModelField(attributionModel, 'cm3_actual_pc')] ||
            0,
        ),
        currentValue: formatPercent(
          deal[getAttributionModelField(attributionModel, 'cm3_actual_pc')] ||
            0,
        ),
        trend: getTrend(
          getAttributionModelField(attributionModel, 'cm3_actual_pc'),
        ),
      },
      {
        key: 'cpm_actual',
        label: 'CPM',
        prevValue: data?.cpm_actual || 0,
        currentValue: deal.cpm_actual || 0,
        trend: getTrend('cpm_actual'),
      },
      {
        key: 'calculated_cpm',
        label: 'Actual CPM',
        prevValue: data?.calculated_cpm || 0,
        currentValue: deal.calculated_cpm || 0,
        trend: getTrend('calculated_cpm'),
      },
      {
        key: getAttributionModelField(attributionModel, 'cpo_actual'),
        label: 'CPO',
        labelCaption: `(${attributionModel})`,
        prevValue:
          data?.[getAttributionModelField(attributionModel, 'cpo_actual')] || 0,
        currentValue:
          deal[getAttributionModelField(attributionModel, 'cpo_actual')] || 0,
        trend: getTrend(
          getAttributionModelField(attributionModel, 'cpo_actual'),
        ),
      },
      {
        key: getAttributionModelField(attributionModel, 'gross_profit_actual'),
        label: 'Gross profit',
        labelCaption: `(${attributionModel})`,
        prevValue: formatCurrency(
          data?.[
            getAttributionModelField(attributionModel, 'gross_profit_actual')
          ] || 0,
        ),
        currentValue: formatCurrency(
          deal[
            getAttributionModelField(attributionModel, 'gross_profit_actual')
          ] || 0,
        ),
        trend: getTrend(
          getAttributionModelField(attributionModel, 'gross_profit_actual'),
        ),
      },
      {
        key: getAttributionModelField(attributionModel, 'avg_basket_actual'),
        label: 'Avg basket',
        labelCaption: `(${attributionModel})`,
        prevValue: formatCurrency(
          data?.[
            getAttributionModelField(attributionModel, 'avg_basket_actual')
          ] || 0,
        ),
        currentValue: formatCurrency(
          deal[
            getAttributionModelField(attributionModel, 'avg_basket_actual')
          ] || 0,
        ),
        trend: getTrend(
          getAttributionModelField(attributionModel, 'avg_basket_actual'),
        ),
      },
      {
        key: getAttributionModelField(attributionModel, 'new_customers_ratio'),
        label: 'NCR',
        labelCaption: `(${attributionModel})`,
        prevValue: formatPercent(
          data?.[
            getAttributionModelField(attributionModel, 'new_customers_ratio')
          ] || 0,
        ),
        currentValue: formatPercent(
          deal[
            getAttributionModelField(attributionModel, 'new_customers_ratio')
          ] || 0,
        ),
        trend: getTrend(
          getAttributionModelField(attributionModel, 'new_customers_ratio'),
        ),
      },
      {
        key: getAttributionModelField(attributionModel, 'orders_nb_actual'),
        label: 'Orders number',
        labelCaption: `(${attributionModel})`,
        prevValue:
          data?.[
            getAttributionModelField(attributionModel, 'orders_nb_actual')
          ] || 0,
        currentValue:
          deal[
            getAttributionModelField(attributionModel, 'orders_nb_actual')
          ] || 0,
        trend: getTrend(
          getAttributionModelField(attributionModel, 'orders_nb_actual'),
        ),
      },
    ],
    [data, deal, getTrend, attributionModel],
  );

  return (
    <ColorCard color={colors.orange}>
      <CardHeader
        title="Deals comparison"
        subheader={
          <Typography variant="inherit">
            Comparison of actual metrics with{' '}
            <Link
              variant="inherit"
              underline="hover"
              href={appLinks.deals.details.as(data?.deal_id || '')}
              target="_blank"
            >
              latest deal
            </Link>
          </Typography>
        }
      />
      <CardContent>
        {error ? (
          <Typography variant="subtitle1">
            Failed to load latest deal
          </Typography>
        ) : (
          <Table>
            <TableHead>
              <TableRow>
                <TableCell />
                <TableCell align="right">Current</TableCell>
                <TableCell align="right">Previous</TableCell>
                <TableCell align="right">Trend</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {!data &&
                tableFields.map((row) => (
                  <TableRow
                    key={row.key}
                    sx={{ '&:last-child td, &:last-child th': { border: 0 } }}
                  >
                    <TableCell component="th" scope="row">
                      <Skeleton variant="text" />
                    </TableCell>
                    <TableCell align="right">
                      <Skeleton variant="text" />
                    </TableCell>
                    <TableCell align="right">
                      <Skeleton variant="text" />
                    </TableCell>
                    <TableCell align="right">
                      <Skeleton
                        variant="rectangular"
                        sx={(theme) => ({
                          borderRadius: `${theme.shape.borderRadius}px`,
                        })}
                      />
                    </TableCell>
                  </TableRow>
                ))}
              {data &&
                tableFields.map((row) => (
                  <TableRow
                    key={row.key}
                    sx={{ '&:last-child td, &:last-child th': { border: 0 } }}
                  >
                    <TableCell component="th" scope="row">
                      {row.label}
                      {row.labelCaption && (
                        <Typography
                          component="span"
                          variant="caption"
                          color="text.secondary"
                          ml={1}
                        >
                          {row.labelCaption}
                        </Typography>
                      )}
                    </TableCell>
                    <TableCell align="right">
                      <Typography color="text.secondary" variant="inherit">
                        {row.currentValue}
                      </Typography>
                    </TableCell>
                    <TableCell align="right">
                      <Typography color="text.secondary" variant="inherit">
                        {row.prevValue}
                      </Typography>
                    </TableCell>
                    <TableCell align="right">
                      <TooltipChip
                        title="Trend"
                        color={row.trend === 'up' ? 'success' : 'warning'}
                        sx={{
                          '& span': {
                            display: 'flex',
                            padding: 1,
                          },
                        }}
                        label={
                          row.trend === 'up' ? (
                            <TrendingUpRounded />
                          ) : (
                            <TrendingDownRounded />
                          )
                        }
                      />
                    </TableCell>
                  </TableRow>
                ))}
            </TableBody>
          </Table>
        )}
      </CardContent>
    </ColorCard>
  );
};

export default DealComparisonCard;
